import { useContext, useMemo } from 'react'
import { generatePath } from 'react-router-dom'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PaymentAgentKey } from 'api/Wallet/Types/PaymentAgentDto'
import { Link } from 'components/Link'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const NameCell: React.FC<GridCellProps> = (props) => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const canViewPaymentAgent = useMemo(() => {
    return hasPermission(permissions, PermissionsEnum.CanViewPaymentAgent)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (canViewPaymentAgent) {
    return (
      <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
        <Link
          to={{
            pathname: generatePath(links.recordsPaymentAgents.detailsUrl as string, {
              id: props.dataItem.id,
            }),
          }}
        >
          {props.dataItem.name}
        </Link>
      </td>
    )
  }

  return <td>{props.dataItem.name}</td>
}

const paymentAgentColumn: GridColumn[] = [
  {
    title: 'Name',
    field: PaymentAgentKey.name,
    cell: NameCell,
    show: true,
    filter: 'text',
  },
  {
    title: 'Client',
    field: PaymentAgentKey.client,
    cell: (props: GridCellProps) => (
      <td
        className='transaction-columns__content'
        style={{ color: 'var(--link)', textDecoration: 'underline' }}
      >
        <Link
          state={{ redirectTo: links.recordsPaymentAgents.url }}
          to={{
            pathname: generatePath(links.recordsClients.detailsUrl as string, {
              id: props.dataItem.client.id,
              slug: 'details',
            }),
          }}
        >
          {props.dataItem.client.name}
        </Link>
      </td>
    ),
    show: true,
    filterable: true,
    filter: 'text',
  },
  {
    title: 'Website',
    field: PaymentAgentKey.website,
    show: true,
    filterable: true,
    filter: 'text',
  },
  {
    title: 'Limits',
    show: true,
    field: 'limits',
    children: [
      {
        title: 'Single',
        field: PaymentAgentKey.singleTransactionLimit,
        filterable: true,
        className: 'table-columns__number',
        filter: 'text',
      },
      {
        title: 'Daily',
        field: PaymentAgentKey.dailyTransactionLimit,
        filterable: true,
        className: 'table-columns__number',
        filter: 'text',
      },
    ],
  },

  {
    title: "Today's Transaction",
    field: PaymentAgentKey.todaysTransaction,
    show: true,
    filterable: true,
    className: 'table-columns__number',
    filter: 'text',
  },

  {
    title: 'POA Email',
    field: PaymentAgentKey.powerOfAttorneyEmail,
    show: true,
    filterable: true,
    filter: 'text',
  },
  {
    title: 'Withdrawal Email',
    field: PaymentAgentKey.withdrawalEmail,
    show: true,
    filterable: true,
    filter: 'text',
  },
  {
    title: 'Active',
    field: PaymentAgentKey.isActive,
    cell: (props: GridCellProps) => (
      <td style={{ textAlign: 'center' }}>
        <input type='checkbox' checked={props.dataItem.isActive} disabled />
      </td>
    ),
    show: true,
    filterable: true,
  },
]

export const paymentAgentFilterColumn: GridColumn[] = [
  {
    title: 'Name',
    field: PaymentAgentKey.name,
    cell: NameCell,
    show: true,
    filter: 'text',
  },
  {
    title: 'Client',
    field: PaymentAgentKey.client,
    cell: (props: GridCellProps) => (
      <td
        className='transaction-columns__content'
        style={{ color: 'var(--link)', textDecoration: 'underline' }}
      >
        <Link
          state={{ redirectTo: links.recordsPaymentAgents.url }}
          to={{
            pathname: generatePath(links.recordsClients.detailsUrl as string, {
              id: props.dataItem.client.id,
              slug: 'details',
            }),
          }}
        >
          {props.dataItem.client.name}
        </Link>
      </td>
    ),
    show: true,
    filterable: true,
    filter: 'text',
  },

  {
    title: 'Active',
    field: PaymentAgentKey.isActive,
    cell: (props: GridCellProps) => (
      <td style={{ textAlign: 'center' }}>
        <input type='checkbox' checked={props.dataItem.isActive} disabled />
      </td>
    ),
    show: true,
    filterable: true,
  },
]

export default paymentAgentColumn
